import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price and keeps it synchronized with
 * the selected variant on product pages and in the quick-add modal.
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product selection and updates the displayed price.
   * Invalid intermediate option combinations retain the last valid price instead
   * of rendering an empty net price and a 0.00 VAT-inclusive price.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct, resource } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // Selecting the first option can temporarily create an unavailable
        // combination until the second option is selected. Keep the last valid
        // price during that intermediate state.
        if (!resource) return;

        const currentPriceContainer = /** @type {HTMLElement | null} */ (
          this.querySelector('[ref="priceContainer"]')
        );
        if (!currentPriceContainer) return;

        const blockId = this.dataset.blockId || '';
        let newProductPrice = blockId
          ? html.querySelector(`.product-details product-price[data-block-id="${blockId}"]`)
          : null;

        if (!newProductPrice) {
          const candidates = Array.from(html.querySelectorAll('.product-details product-price'));
          newProductPrice =
            candidates.find(
              (candidate) =>
                !this.dataset.productId ||
                (candidate instanceof HTMLElement && candidate.dataset.productId === this.dataset.productId)
            ) ||
            candidates[0] ||
            null;
        }

        if (!newProductPrice) return;

        const newPriceSource = newProductPrice.querySelector('[ref="priceContainer"]');
        if (!newPriceSource) return;

        const newPrice = /** @type {HTMLElement} */ (newPriceSource.cloneNode(true));
        currentPriceContainer.replaceWith(newPrice);

        const currentVolumePricingNote = /** @type {HTMLElement | null} */ (
          this.querySelector('[ref="volumePricingNote"]')
        );
        const newNoteSource = newProductPrice.querySelector('[ref="volumePricingNote"]');

        if (!newNoteSource) {
          currentVolumePricingNote?.remove();
        } else {
          const newNote = /** @type {HTMLElement} */ (newNoteSource.cloneNode(true));
          if (currentVolumePricingNote) currentVolumePricingNote.replaceWith(newNote);
          else newPrice.insertAdjacentElement('afterend', newNote);
        }

        // Update the payment-terms variant ID after the price has been refreshed.
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = resource.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
